/** Placing things on a `GenState`: the draws, the retries, the payloads, the
 * guards, and the bookkeeping that keeps object ids positional.
 *
 * `place.ts` answers whether ONE object fits at ONE cell without breaking the
 * map. This module is the layer above it: the danger field the tables read as
 * risk, the crowd-biased cell draw, the retry loop over a cell pool, the
 * payload writers (monsters, garrisons, artifacts, shrines), the doorstep
 * guards, and `dropObject`/`renumber`. Every populating pass - surface,
 * neutral towns, gates, underworld - is written in terms of these.
 */

import type {
  ArtifactDef, CreatureDef, FactionId, SpellDef, SpellDefId, TerrainKind,
} from "@game/core";
import type { AdventureMapState } from "@game/core";
import { setOccupant } from "@game/core";
import type { Zone } from "./biomes.js";
import type { Grid } from "./grid.js";
import { allNeighbours, chebyshev, orthoNeighbours, terrainOf, walkable, xOf, yOf } from "./grid.js";
import { crowdWeight } from "./landform.js";
import { GUARDS_BY_DANGER, wandererWeight, wanderersByDangerBand } from "./catalog.js";
import { pickThemed, scatterPool } from "./pools.js";
import type { Payload } from "./place.js";
import { objectIdFor, placeCore, removePlacement } from "./place.js";
import type { Rng } from "./rng.js";
import { int, pick, range, weighted } from "./rng.js";
import type { GenState } from "./state.js";
import { clamp } from "./state.js";

// ------------------------------------------------------------------- danger

/** Steps from the nearest starting town; the placement tables read it as risk. */
export function dangerField(m: AdventureMapState, g: Grid, towns: readonly number[]): Int32Array {
  const n = g.width * g.height;
  const dist = new Int32Array(n).fill(0x3fffffff);
  const queue: number[] = [];
  for (const t of towns) if (t >= 0 && t < n) { dist[t] = 0; queue.push(t); }
  const buf: number[] = [];
  for (let head = 0; head < queue.length; head++) {
    const cur = queue[head]!;
    const d = dist[cur]! + 1;
    for (const nb of allNeighbours(g, cur, buf)) {
      if (!walkable(m, nb)) continue;
      if (dist[nb]! <= d) continue;
      dist[nb] = d;
      queue.push(nb);
    }
  }
  return dist;
}

export const dangerBand = (s: GenState, i: number): number => {
  const d = s.danger[i] ?? 0;
  return d > 1 << 20 ? 5 : clamp(Math.trunc(d / 9), 0, 5);
};

// ---------------------------------------------------------------- placement

/**
 * How many uniform draws a placement takes before keeping the busiest.
 *
 * A single uniform draw over a zone's cells is a Poisson process, and a Poisson
 * process has no busy quarter and no quiet one - which is the "near-uniform
 * spacing" reading. Best-of-three on `crowdWeight` biases roughly two placements
 * in three into the crowded third of the zone while still leaving the quiet part
 * reachable, so the zone acquires a gradient without acquiring an empty half.
 * A hard mask would give the empty half.
 */
const CROWD_DRAWS = 3;

/** One uniform draw, biased toward the crowded end of the `crowding` field. */
function drawCell(s: GenState, cells: readonly number[]): number {
  let best = cells[int(s.r, cells.length)]!;
  let bw = crowdWeight(xOf(s.g, best), yOf(s.g, best));
  for (let k = 1; k < CROWD_DRAWS; k++) {
    const c = cells[int(s.r, cells.length)]!;
    const w = crowdWeight(xOf(s.g, c), yOf(s.g, c));
    if (w > bw) { best = c; bw = w; }
  }
  return best;
}
/** The cells of `pool` within `radius` of `centre`; `pool` when none are. */
export function around(
  g: Grid, pool: readonly number[], centre: number, radius: number,
): readonly number[] {
  const out: number[] = [];
  for (const i of pool) if (chebyshev(g, i, centre) <= radius) out.push(i);
  return out.length >= 3 ? out : pool;
}

/** How far from a hamlet's centre its things are allowed to stand. */
export const HAMLET_RADIUS = 4;

/**
 * `count` places in this zone that things will bunch around.
 *
 * Drawn through `drawCell`, so the hamlets themselves land in the busy part of
 * the `crowding` field and the zone gets both a gradient and a grain: a couple
 * of dense spots inside a generally busier half.
 */
export function hamlets(s: GenState, cells: readonly number[], count: number): number[] {
  const out: number[] = [];
  for (let k = 0; k < count; k++) {
    if (cells.length === 0) break;
    out.push(drawCell(s, cells));
  }
  return out;
}

/** The neighbourhood of one hamlet picked at random, or the whole zone. */
export function near(s: GenState, cells: readonly number[], spots: readonly number[]): readonly number[] {
  const h = pick(s.r, spots);
  return h === null ? cells : around(s.g, cells, h, HAMLET_RADIUS);
}

/** One placement attempt loop. Returns the index in `objects`, or -1. */
export function place(
  s: GenState, cells: readonly number[], defId: string,
  payloadFor: () => Payload, attempts = 30,
): number {
  if (cells.length === 0) return -1;
  for (let k = 0; k < attempts; k++) {
    const cell = drawCell(s, cells);
    const at = placeCore(s, defId, xOf(s.g, cell), yOf(s.g, cell), payloadFor());
    if (at >= 0) return at;
  }
  return -1;
}

export const nothing = (): Payload => ({});

/**
 * A month of the creature's own growth, scaled by danger band and the
 * template's `monsterStrengthBp`. Using growth rather than a hand-authored
 * default count is what lets a Spearwarden stack and a Crimson Wyrm stack both
 * come out of the same formula: growth 12 × 4 is 48, growth 1 × 4 is 4.
 */
function monsterPayload(s: GenState, creature: CreatureDef, band: number): Payload {
  const base = Math.max(1, creature.growth * 4);
  const scale = 5000 + band * 1400;
  const n = Math.max(1, Math.round((base * scale * s.monsterBp) / (10000 * 10000)));
  const jitter = Math.max(1, Math.round(n / 5));
  const gold = creature.cost.gold ?? 0;
  return {
    creature: creature.id as string,
    count: Math.max(1, n + range(s.r, -jitter, jitter)),
    joinCostPerHead: gold,
    flees: creature.tier < 7,
  };
}

export function pickWanderer(
  s: GenState, band: number, terrain: TerrainKind | null, rng: Rng,
  theme: FactionId | null = null,
): CreatureDef | null {
  const pool = wanderersByDangerBand(s.content)[clamp(band, 0, 5)] ?? [];
  if (pool.length === 0) return null;
  if (terrain === null && theme === null) return pick(rng, pool);
  const t = terrain ?? "grass";
  return weighted(rng, pool, (c) => wandererWeight(s.content, c, t, theme));
}

export const poolOf = (s: GenState) => scatterPool(s.content);

export const themed = (s: GenState, ids: readonly string[], zone: Zone): string | null =>
  pickThemed(s.r, ids, s.content, zone.theme, zone.terrain);

const zoneAt = (s: GenState, cell: number): Zone | undefined =>
  s.zones[s.zoneOf[cell] ?? -1];

/**
 * A checkpoint with somebody at it. The guard is the band's, drawn from the
 * same table the doorsteps use, and one garrison in four is anti-magic. For a
 * whole build the payload was `{ antiMagic: false }` and nothing else, which
 * the kernel read as a wall that never fought.
 */
export function garrisonPayload(s: GenState, band: number): Payload {
  // Rolled on the side stream: a garrison that drew from `s.r` re-dealt every
  // placement after it, on every seed the tests had picked for an underground.
  const antiMagic = int(s.side, 4) === 0;
  const creature = pickWanderer(s, band, null, s.side);
  if (creature === null) return { antiMagic };
  const base = Math.max(1, creature.growth * 4);
  const n = Math.max(1, Math.round((base * (5000 + band * 1400) * s.monsterBp) / (10000 * 10000)));
  const jitter = Math.max(1, Math.round(n / 5));
  return {
    antiMagic,
    creature: creature.id as string,
    count: Math.max(1, n + range(s.side, -jitter, jitter)),
  };
}

export function placeGuard(s: GenState, cell: number, band: number): number {
  // Sprite first, on the placement stream, from the same nineteen-row table
  // as before. The creature is a payload and is rolled on `s.side`, so a
  // Spearwarden stack can stand on an Imp sprite without re-dealing the map.
  const tier = GUARDS_BY_DANGER[clamp(band, 0, GUARDS_BY_DANGER.length - 1)] ?? [];
  const defId = pick(s.r, tier);
  if (defId === null) return -1;
  const z = zoneAt(s, cell);
  const creature = pickWanderer(
    s, band, terrainOf(s.map, cell), s.side, z?.theme ?? null,
  );
  if (creature === null) return -1;
  return place(s, [cell], defId, () => monsterPayload(s, creature, band), 1);
}

/**
 * A guard for whatever was just placed, standing on its doorstep.
 *
 * Returns whether one actually got there. A doorstep with no free orthogonal
 * neighbour - the object is in a corner, or the road runs past its door, or the
 * scatter already filled the cell - takes no guard, and for most of the things
 * this is called on that is only a pity. For a few it is a defect, and the
 * caller has to be able to tell the difference, which is what the boolean is
 * for: see `placeCaskets`.
 */
export function guardAt(s: GenState, objectIndex: number, band: number): boolean {
  if (objectIndex < 0) return false;
  const entrance = s.placements[objectIndex]?.entrances[0];
  if (entrance === undefined) return false;
  const buf: number[] = [];
  const spots = orthoNeighbours(s.g, entrance, buf).filter(
    (i) => walkable(s.map, i) && s.map.occupant[i] === 0 && s.map.road[i] === 0,
  );
  if (spots.length === 0) return false;
  return placeGuard(s, spots[int(s.r, spots.length)]!, band) >= 0;
}

export function artifactPayload(s: GenState, band: number): Payload {
  const ranks = band >= 4 ? ["relic", "major"]
    : band >= 2 ? ["major", "minor"]
      : ["treasure", "minor"];
  const pool = [...s.content.artifacts.values()]
    .filter((a: ArtifactDef) => ranks.includes(a.rank) && a.components.length === 0)
    .map((a) => a.id as string)
    .sort();
  const id = pick(s.r, pool);
  return id === null ? {} : { artifact: id };
}

export function shrinePayload(s: GenState, band: number): Payload {
  // `dangerBand` is 0..5, so `1 + band / 2` topped out at level 3 and no
  // shrine on any map could teach a level-4 or level-5 spell; the top band
  // now reaches the top level.
  const maxLevel = clamp(1 + Math.trunc((band * 4) / 5), 1, 5);
  const pool = [...s.content.spells.values()]
    // A Shrine teaches; an `artifactOnly` spell is never taught.
    .filter((sp: SpellDef) => sp.level <= maxLevel && sp.artifactOnly !== true)
    .map((sp) => sp.id as string)
    .sort();
  const id = pick(s.r, pool);
  if (id === null) return {};
  return { spell: id, level: s.content.spells.get(id as SpellDefId)?.level ?? 1 };
}

// ------------------------------------------------------------- bookkeeping

/** Ids are positional, so dropping one object renumbers everything after it. */
export function dropObject(s: GenState, index: number): void {
  const p = s.placements[index];
  if (p === undefined) return;
  removePlacement(s.map, p);
  s.objects.splice(index, 1);
  s.placements.splice(index, 1);
  renumber(s);
}

export function renumber(s: GenState): void {
  for (let i = 0; i < s.objects.length; i++) {
    const o = s.objects[i]!;
    const want = objectIdFor(s.idBase + i + 1);
    if (o.id !== want) s.objects[i] = { ...o, id: want };
  }
  for (let i = 0; i < s.placements.length; i++) {
    const p = s.placements[i]!;
    const id = s.objects[i]?.id;
    if (id === undefined) continue;
    for (const cell of p.cells) {
      const blocks = p.def.blocksMovement && !p.entrances.includes(cell);
      // `s.map` is this level's own single plane; `assemble` re-stamps the
      // finished list onto the real z.
      setOccupant(s.map, xOf(s.g, cell), yOf(s.g, cell), 0, id, blocks);
    }
  }
}
